import { Button } from "../ui/button"
import { Zap, HeartHandshake, ShieldCheck } from "lucide-react"
import { MatrixRain } from "../ui/matrix-rain"
import { FadeIn } from "../ui/fade-in"
import { EditorsSection } from "./EditorsSection"

export function HeroSection() {
  return (
      <section id="hero" className="relative min-h-screen pt-20 sm:pt-32 pb-16 overflow-hidden bg-black">
        {/* Matrix Rain Background */}
        <MatrixRain className="opacity-40" />

        {/* Background Elements */}
        <div className="absolute inset-0 overflow-hidden">
          <div className="absolute top-0 left-0 right-0 h-[500px] bg-gradient-to-b from-red-600/20 to-transparent"></div>
          <div className="absolute inset-0 opacity-30">
            <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-red-600/30 rounded-full filter blur-[100px]"></div>
            <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-red-600/20 rounded-full filter blur-[120px]"></div>
          </div>
          {/* Vignette effect at the bottom */}
          <div className="absolute bottom-0 left-0 right-0 h-[200px] bg-gradient-to-t from-black to-transparent"></div>
        </div>

        <div className="relative z-10 w-full px-4 sm:px-6 md:px-8">
          <div className="text-center py-8 sm:py-16 mx-auto max-w-5xl">
            {/* Security Badges */}
            <FadeIn direction="down" duration={0.6}>
              <div className="flex flex-wrap justify-center gap-3 mb-6 sm:mb-8">
                <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-red-600/20">
                  <ShieldCheck className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
                  <span className="text-xs sm:text-sm font-medium">Expertise</span>
                </div>

                <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-red-600/20">
                  <HeartHandshake className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
                  <span className="text-xs sm:text-sm font-medium">Accompagnement</span>
                </div>

                <div className="flex items-center gap-2 bg-black/30 backdrop-blur-sm px-3 py-1.5 sm:px-4 sm:py-2 rounded-full border border-red-600/20">
                  <Zap className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-red-500" />
                  <span className="text-xs sm:text-sm font-medium">Performance</span>
                </div>
              </div>
            </FadeIn>

            <FadeIn direction="up" duration={0.7} delay={0.1}>
              <h1 className="text-3xl sm:text-4xl md:text-6xl lg:text-7xl font-bold mb-4 sm:mb-6 tracking-tight leading-tight font-orbitron">
                La cybersécurité au service{" "}
                <span className="block sm:inline font-orbitron">
                  de nos
                  <span className="text-red-600 font-orbitron"> partenaires</span>
                </span>
              </h1>
            </FadeIn>

            <FadeIn direction="up" duration={0.7} delay={0.2}>
              <p className="text-gray-400 text-base sm:text-lg md:text-xl mb-6 sm:mb-8 max-w-2xl mx-auto px-2">
                Chez <span className="font-bold">Secure&More</span>, nous protégeons vos systèmes et données avec des solutions de cybersécurité sur mesure, conçues pour les défis internationals.
              </p>
            </FadeIn>

            <FadeIn direction="up" duration={0.7} delay={0.3}>
              <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center mb-8 sm:mb-12 px-4 sm:px-0">
                <Button variant="primary" size="lg" className="font-bold w-full sm:w-auto text-sm sm:text-base py-2.5 sm:py-3">
                  Nous contacter
                </Button>
                <Button
                    variant="outline"
                    size="lg"
                    className="gap-2 w-full sm:w-auto text-sm sm:text-base py-2.5 sm:py-3"
                >
                  Découvrir nos services
                </Button>
              </div>
            </FadeIn>

            {/* Stats */}
            <FadeIn direction="up" duration={0.7} delay={0.4}>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 md:gap-8 max-w-3xl mx-auto mt-8 sm:mt-16 px-1 sm:px-0">
                <div className="text-center p-2 sm:p-4 bg-black/30 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/10 hover:border-red-600/40 duration-300">
                  <div className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">99,9%</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Garantie de Disponibilité</div>
                </div>

                <div className="text-center p-2 sm:p-4 bg-black/30 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/10 hover:border-red-600/40 duration-300">
                  <div className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">24/7</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Support</div>
                </div>

                <div className="text-center p-2 sm:p-4 bg-black/30 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/10 hover:border-red-600/40 duration-300">
                  <div className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">10k+</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Certificats</div>
                </div>

                <div className="text-center p-2 sm:p-4 bg-black/30 backdrop-blur-sm rounded-lg sm:rounded-xl border border-white/10 hover:border-red-600/40 duration-300">
                  <div className="text-xl sm:text-3xl md:text-4xl font-bold text-white mb-1 sm:mb-2">500+</div>
                  <div className="text-gray-400 text-xs sm:text-sm">Clients Entreprises</div>
                </div>
              </div>
            </FadeIn>
          </div>
        </div>
        <EditorsSection/>
      </section>
  )
}
